import { getUrl } from "@insite/client-framework/ServerSideRendering";
import { Website } from "@insite/client-framework/Services/WebsiteService";
import { BreadCrumbModel } from "@insite/client-framework/Types/ApiModels";

export interface Location {
    pathname: string;
    search: string;
}

export interface BaseStructuredDataProps {
    website: Website;
    location: Location;
}

export interface BreadcrumbData {
    text: string;
    url: string;
    categoryId: string;
}

export interface GenericStructuredDataProps extends BaseStructuredDataProps {
    pageTitle: string;
    pageType?: "WebPage" | "CollectionPage" | "ItemPage";
    breadcrumbLinks?: Array<{ text: string; url: string }>;
    additionalEntities?: Array<{
        "@type": string;
        "@id": string;
        [key: string]: any;
    }>;
    webPageProperties?: {
        about?: string;
        mainEntity?: string;
        alternateName?: string;
        [key: string]: any;
    };
    includeOrganization?: boolean;
}

export const getAuthorityAndUrls = (location: Location) => {
    const url: { protocol: string; host: string } = IS_SERVER_SIDE ? getUrl()! : window.location;
    const authority = `${url.protocol}//${url.host}`;
    const currentUrl = `${authority}${location.pathname}${location.search}`;

    return {
        authority,
        currentUrl,
        pageId: `${currentUrl}#webpage`,
        websiteId: `${authority}#website`,
        breadcrumbId: `${currentUrl}#breadcrumb`,
    };
};

export const prepareBreadcrumbData = (
    breadCrumbs: BreadCrumbModel[] | null | undefined,
    authority: string,
    currentUrl: string,
    pageName?: string,
) => {
    // Prepare breadcrumb data - filter out breadcrumbs with null/undefined URLs
    const breadcrumbData =
        breadCrumbs
            ?.map((breadcrumb, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: breadcrumb.text,
                item: breadcrumb.url ? authority + breadcrumb.url : undefined,
            }))
            .filter(breadcrumb => breadcrumb.item) || [];

    // Add the current page as the last breadcrumb item if it's not already there
    if (pageName) {
        const lastBreadcrumb = breadcrumbData[breadcrumbData.length - 1];
        const isPageAlreadyInBreadcrumbs =
            lastBreadcrumb && lastBreadcrumb.name === pageName && lastBreadcrumb.item === currentUrl;

        if (!isPageAlreadyInBreadcrumbs) {
            breadcrumbData.push({
                "@type": "ListItem",
                position: breadcrumbData.length + 1,
                name: pageName,
                item: currentUrl,
            });
        }
    }

    return breadcrumbData;
};

export const createWebSiteObject = (authority: string, website: Website, websiteId: string) => ({
    "@type": "WebSite",
    "@id": websiteId,
    url: authority,
    name: website.companyName,
});

export const createBreadcrumbListObject = (breadcrumbId: string, breadcrumbData: any[]) => ({
    "@type": "BreadcrumbList",
    "@id": breadcrumbId,
    itemListElement: breadcrumbData,
});

export const prepareStructuredPageData = ({
    pageTitle,
    pageType = "WebPage",
    breadcrumbLinks,
    additionalEntities = [],
    webPageProperties = {},
    includeOrganization = true,
    website,
    location,
}: GenericStructuredDataProps) => {
    const { authority, currentUrl, pageId, websiteId, breadcrumbId } = getAuthorityAndUrls(location);

    // Prepare breadcrumb data if breadcrumb links are provided
    const breadcrumbData = breadcrumbLinks?.length
        ? prepareBreadcrumbData(
              breadcrumbLinks.map(link => ({ text: link.text, url: link.url, categoryId: "" })),
              authority,
              currentUrl,
              pageTitle,
          )
        : [];

    // Base entities that are always included
    const baseEntities = [
        createWebSiteObject(authority, website, websiteId),
        ...(includeOrganization
            ? [
                  {
                      "@type": "Organization",
                      "@id": `${authority}#organization`,
                      name: website.companyName,
                      url: authority,
                  },
              ]
            : []),
        {
            "@type": pageType,
            "@id": pageId,
            url: currentUrl,
            name: pageTitle,
            isPartOf: { "@id": websiteId },
            ...(breadcrumbData.length > 0 ? { breadcrumb: { "@id": breadcrumbId } } : {}),
            ...webPageProperties,
        },
    ];

    // Add breadcrumb list if breadcrumbs exist
    const breadcrumbEntity =
        breadcrumbData.length > 0 ? [createBreadcrumbListObject(breadcrumbId, breadcrumbData)] : [];

    // Combine all entities
    const allEntities = [...baseEntities, ...breadcrumbEntity, ...additionalEntities];

    const data = {
        "@context": "https://schema.org",
        "@graph": allEntities,
    };

    return JSON.stringify(data);
};
